import { Link } from 'react-router-dom'
import { formatLocalDateTime, formatVenueTime } from '../lib/datetime.js'
import { conferenceLabel } from '../lib/text.js'
import { isPastEvent } from '../lib/dateRange.js'
import { placeHref, tagHref, yearHref } from '../lib/permalinks.js'
import useCurrentYear from '../hooks/useCurrentYear.js'
import TagPill from './TagPill.jsx'
import PastPill from './PastPill.jsx'
import PlaceLink from './PlaceLink.jsx'
import TalkBody from './TalkBody.jsx'

function dayParts(iso) {
  if (!iso) return { weekday: '', monthDay: '', time: '' }
  const d = new Date(iso)
  const weekday = new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(d)
  const monthDay = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(d)
  const time = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(d)
  return { weekday, monthDay, time }
}

// #350: a standalone talk and a conference are the same card family.
//
// This card used to be its own visual language — a multi-line abstract, an h-9
// avatar and a text-lg speaker name, `Your local:` on a line of its own — while
// the identical event inside a ConferenceCard was rendered dense and compact.
// Side by side in one feed that read as two products. The structure is now
// exactly ConferenceCard's: rail, tag row, title, grey sub-line, body, then one
// footer line carrying place and times. The body is the shared TalkBody, so the
// two cannot drift apart again.
export default function EventCard({ event }) {
  const location = [event.city, event.region, event.country].filter(Boolean).join(', ')
  const { weekday, monthDay, time } = dayParts(event.startsAtUtc)
  const detailHref = `/events/${event.id}`
  // #334: a year view mixes past and future, so a card has to say which it is.
  // Judge on the end when there is one — a talk running right now is not past.
  const past = isPastEvent({ startsAtUtc: event.endsAtUtc || event.startsAtUtc })
  // #338: permalinks preserve the year you are browsing, so narrowing by
  // speaker from inside /2026 keeps you in 2026.
  const year = useCurrentYear()
  const place = placeHref(event, year)

  return (
    <article className="group relative overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200/60 transition hover:shadow-lg hover:ring-brand-200 sm:hover:-translate-y-0.5">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-brand-500 to-brand-300 opacity-0 transition group-hover:opacity-100" />

      <div className="flex flex-col gap-5 p-5 sm:flex-row sm:p-6">
        {/* #311: the rail is a FIXED width, not `min-w`. With a minimum the box
            sized itself to its widest child, so "Sep 16" made a wider rail than
            "Aug 25" and "10:20 AM" a wider one than "3:00 AM" — every distinct
            date produced a slightly different box and the column's inner edge
            came out ragged. w-[128px] clears the widest realistic content
            ("Sep 30" at sm:text-3xl) with room to spare; shrink-0 stops the
            flex row squeezing it back when the title is long. The `sm:`
            prefixes mean the mobile row layout is untouched. */}
        {/* #338: the date is the year element, so the rail is the year's
            permalink. `relative z-10` lifts it above the card's stretched
            title link, which otherwise swallows every click inside the card. */}
        <Link
          to={yearHref(event.startsAtUtc) || '/'}
          title={`All events in ${new Date(event.startsAtUtc).getUTCFullYear()}`}
          className={`relative z-10 flex items-start gap-3 transition sm:w-[128px] sm:shrink-0 sm:flex-col sm:items-center sm:justify-center sm:gap-1 sm:rounded-xl sm:px-4 sm:py-3 ${
            past ? 'sm:bg-slate-100 sm:hover:bg-slate-200' : 'sm:bg-brand-50/60 sm:hover:bg-brand-100/70'
          }`}
        >
          <div className={`text-xs font-medium uppercase tracking-wider ${
            past ? 'text-slate-500' : 'text-brand-700'
          }`}>{weekday}</div>
          <div className="whitespace-nowrap text-2xl font-bold leading-none text-slate-900 sm:text-3xl">{monthDay}</div>
          <div className="whitespace-nowrap text-sm text-slate-600 sm:mt-1">{time}</div>
        </Link>

        <div className="min-w-0 flex-1">
          <div className="relative z-10 flex flex-wrap items-center gap-2">
            {past ? <PastPill /> : null}
            {event.format ? (
              <span className="inline-flex items-center rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-medium text-brand-800">
                {event.format.toLowerCase().replace('_', '-')}
              </span>
            ) : null}
            {event.tags?.slice(0, 4).map((t) => (
              <TagPill key={t.slug} tag={t} href={tagHref(t, year)} />
            ))}
            {event.tags && event.tags.length > 4 ? (
              <span className="text-xs text-slate-500">+{event.tags.length - 4}</span>
            ) : null}
          </div>

          <h2 className="mt-2 text-lg font-semibold leading-snug tracking-tight text-slate-900 transition group-hover:text-brand-700 sm:text-xl">
            {/* Stretched-link pattern: this anchor's hit area extends to the
                whole card (parent is `relative`). The body's links and action
                bar use `relative z-10` so they stay clickable on top of it. */}
            <Link to={detailHref} className="static before:absolute before:inset-0 before:content-['']">
              {event.title}
            </Link>
          </h2>
          {/* The conference name sits where a ConferenceCard puts its venue:
              the grey line under the heading that says where this belongs. */}
          {conferenceLabel(event) ? (
            <div className="mt-0.5 text-sm text-slate-500">{conferenceLabel(event)}</div>
          ) : null}

          {/* Title is already the card heading, so the body renders without it. */}
          <TalkBody talk={event} year={year} showTitle={false} />

          {/* One footer line, as on a conference card. `Your local:` used to
              own a line of its own here, which is most of what made this card
              look taller and looser than its conference sibling. */}
          <div className="relative z-10 mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
            {location ? (
              <PlaceLink href={place}>{location}</PlaceLink>
            ) : null}
            {event.tzId ? (
              <span className="text-xs text-slate-500">
                Venue local: {formatVenueTime(event.startsAtUtc, event.tzId)} ({event.tzId})
              </span>
            ) : null}
            <span className="text-xs text-slate-400">
              Your local: {formatLocalDateTime(event.startsAtUtc)}
            </span>
          </div>
        </div>
      </div>
    </article>
  )
}
